import React, { useState, useCallback, useEffect } from 'react';
import { Box, Flex, Text, Button, Select, Card, Badge, TextArea, Heading, TextField } from '@radix-ui/themes';
import { PaperPlaneIcon, ReloadIcon } from '@radix-ui/react-icons';
import toast from 'react-hot-toast';
import AdminLayout from './AdminLayout';
import SmartMeterPanel from './SmartMeterPanel';
import { apiRequest, tellihealthApi } from '../../services/api';
import TimeInput12h from '../common/TimeInput12h';
import { useOrganization } from '../../contexts/OrganizationContext';

const READING_TYPES = ['BloodPressure', 'BloodGlucose', 'Weight', 'SpO2', 'Temperature', 'PeakFlow'] as const;
const EVENT_TYPES = ['Heartbeat', 'ReminderCleared', 'SettingsResponse'] as const;

const DEFAULT_PAYLOADS: Record<string, object> = {
  BloodPressure: {
    data: { systolic: 120, diastolic: 80, pulse: 72, arrhythmia: 0 },
    deviceId: '864593054464085',
    readingType: 'BloodPressure',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    mem: '0',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  BloodGlucose: {
    data: { glucose: 110, readingPeriod: 'before', unit: 'mg/dL' },
    deviceId: '864593054464085',
    readingType: 'BloodGlucose',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    mem: '0',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  Weight: {
    data: { weight: 165, unit: 'lb' },
    deviceId: '864593054464085',
    readingType: 'Weight',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    mem: '0',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  SpO2: {
    data: { spo2: 98, pulse: 72, battery: 85, rsrp: 24, pi: 20 },
    deviceId: '864593054464085',
    readingType: 'SpO2',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    mem: '0',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  Temperature: {
    data: { temperature: 98.6, unit: 'F' },
    deviceId: '864593054464085',
    readingType: 'Temperature',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    mem: '0',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  PeakFlow: {
    data: { fev1: 2.5, pef: 400 },
    deviceId: '864593054464085',
    readingType: 'PeakFlow',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    mem: '0',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  Heartbeat: {
    type: 'Heartbeat',
    deviceId: '864593054464085',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  ReminderCleared: {
    type: 'ReminderCleared',
    deviceId: '864593054464085',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    devdata: { r: '2', batt: '8', cycles: '0' },
  },
  SettingsResponse: {
    type: 'SettingsResponse',
    deviceId: '864593054464085',
    time: String(Date.now()),
    tzOffsetMinutes: '-300',
    genVer: '2',
    status: 'ACK',
  },
};

interface StatusData {
  readings: { total: number; byType: Array<{ reading_type: string; count: number; last_received: string }> };
  events: { total: number; byType: Array<{ event_type: string; count: number; last_received: string }> };
}

interface DeviceOption {
  device_id: string;
  patient_id: string | null;
  patient_name: string | null;
  organization_name: string | null;
  active: boolean;
}


const AdminTellihealthPage: React.FC = () => {
  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;
  // Mode
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');

  // Simple mode state
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [bpDate, setBpDate] = useState('');
  const [bpTime, setBpTime] = useState('');
  const [systolic, setSystolic] = useState(120);
  const [diastolic, setDiastolic] = useState(80);
  const [pulse, setPulse] = useState(72);
  const [simpleSending, setSimpleSending] = useState(false);
  const [simpleResponse, setSimpleResponse] = useState<{ success: boolean; message: string } | null>(null);

  // Advanced mode state
  const [endpointType, setEndpointType] = useState<'readings' | 'events'>('readings');
  const [selectedType, setSelectedType] = useState<string>('BloodPressure');
  const [payload, setPayload] = useState<string>(JSON.stringify(DEFAULT_PAYLOADS['BloodPressure'], null, 2));
  const [sending, setSending] = useState(false);
  const [lastResponse, setLastResponse] = useState<string>('');

  // Status
  const [status, setStatus] = useState<StatusData | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const response = await apiRequest('/webhooks/tellihealth/status');
      const data = await response.json();
      setStatus(data);
    } catch {
      toast.error('Failed to fetch status');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const fetchDevices = useCallback(async () => {
    setLoadingDevices(true);
    try {
      const filterArg = !sessionOrgId || sessionOrgId === 'ALL' ? undefined : sessionOrgId;
      const data = await tellihealthApi.listDevices(filterArg);
      const deviceList = data.devices || data || [];
      setDevices(deviceList);
      if (deviceList.length > 0 && !selectedDeviceId) {
        setSelectedDeviceId(deviceList[0].device_id);
      } else if (deviceList.length === 0) {
        setSelectedDeviceId('');
      }
    } catch {
      toast.error('Failed to load devices');
    } finally {
      setLoadingDevices(false);
    }
  }, [sessionOrgId, selectedDeviceId]);

  useEffect(() => {
    fetchStatus();
    const now = new Date();
    setBpDate(now.toISOString().split('T')[0]);
    setBpTime(now.toTimeString().slice(0, 5));
  }, [fetchStatus]);

  useEffect(() => {
    if (!sessionLoaded) return;
    fetchDevices();
  }, [sessionLoaded, fetchDevices]);

  // Simple mode send
  const handleSimpleSend = async () => {
    if (!selectedDeviceId) {
      toast.error('Please select a device');
      return;
    }

    setSimpleSending(true);
    setSimpleResponse(null);

    const dateTime = new Date(`${bpDate}T${bpTime}:00`);
    const epochMs = String(dateTime.getTime());
    const tzOffset = String(-dateTime.getTimezoneOffset());

    const bpPayload = {
      data: { systolic, diastolic, pulse, arrhythmia: 0 },
      deviceId: selectedDeviceId,
      readingType: 'BloodPressure',
      time: epochMs,
      tzOffsetMinutes: tzOffset,
      genVer: '2',
      mem: '0',
      devdata: { r: '2', batt: '8', cycles: '0' },
    };

    try {
      const response = await apiRequest('/webhooks/tellihealth/readings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bpPayload),
      });
      const data = await response.json();
      setSimpleResponse({ success: true, message: 'Blood Pressure reading sent successfully' });
      toast.success('Blood Pressure reading sent successfully');
      fetchStatus();
    } catch (err: any) {
      const message = err?.message || 'Failed to send reading';
      setSimpleResponse({ success: false, message });
      toast.error(message);
    } finally {
      setSimpleSending(false);
    }
  };

  // Advanced mode handlers
  const handleTypeChange = (type: string) => {
    setSelectedType(type);
    const defaultPayload = DEFAULT_PAYLOADS[type];
    if (defaultPayload) {
      const updated = { ...defaultPayload, time: String(Date.now()) };
      setPayload(JSON.stringify(updated, null, 2));
    }
  };

  const handleEndpointChange = (type: 'readings' | 'events') => {
    setEndpointType(type);
    const firstType = type === 'readings' ? 'BloodPressure' : 'Heartbeat';
    setSelectedType(firstType);
    const defaultPayload = DEFAULT_PAYLOADS[firstType];
    if (defaultPayload) {
      const updated = { ...defaultPayload, time: String(Date.now()) };
      setPayload(JSON.stringify(updated, null, 2));
    }
  };

  const handleAdvancedSend = async () => {
    setSending(true);
    setLastResponse('');
    try {
      const parsedPayload = JSON.parse(payload);
      const endpoint = endpointType === 'readings'
        ? '/webhooks/tellihealth/readings'
        : '/webhooks/tellihealth/events';

      const response = await apiRequest(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsedPayload),
      });
      const data = await response.json();
      setLastResponse(JSON.stringify(data, null, 2));
      toast.success(`${selectedType} sent successfully`);
      fetchStatus();
    } catch (err: any) {
      const message = err?.message || 'Failed to send';
      setLastResponse(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const typeOptions = endpointType === 'readings' ? READING_TYPES : EVENT_TYPES;

  return (
    <AdminLayout title="RPM Devices" subtitle="Test webhook endpoints and monitor incoming device data">
      <Flex gap="4" direction={{ initial: 'column', md: 'row' }}>
        {/* Left: Send panel */}
        <Box style={{ flex: 1 }}>
          <Card>
            <Flex direction="column" gap="3">
              {/* Mode toggle */}
              <Flex gap="2">
                <Button
                  variant={mode === 'simple' ? 'solid' : 'soft'}
                  size="2"
                  onClick={() => setMode('simple')}
                >
                  Quick BP Send
                </Button>
                <Button
                  variant={mode === 'advanced' ? 'solid' : 'soft'}
                  size="2"
                  onClick={() => setMode('advanced')}
                >
                  Advanced
                </Button>
              </Flex>

              {mode === 'simple' ? (
                <Flex direction="column" gap="3">
                  {/* Header with device image */}
                  <Flex align="center" gap="4">
                    <img
                      src="/tellihealth-bp-device.png"
                      alt="Tellihealth BP Monitor"
                      style={{
                        width: 80,
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 12,
                      }}
                    />
                    <Box>
                      <Heading size="4">Send Blood Pressure Reading</Heading>
                      <Text size="2" color="gray">Select a device and enter BP values</Text>
                    </Box>
                  </Flex>

                  {/* Device dropdown */}
                  <Box>
                    <Text size="2" weight="bold" mb="1" as="div">Device</Text>
                    {loadingDevices ? (
                      <Text size="2" color="gray">Loading devices...</Text>
                    ) : devices.length === 0 ? (
                      <Text size="2" color="gray">No devices registered. Register devices on the Devices page.</Text>
                    ) : (
                      <Select.Root value={selectedDeviceId} onValueChange={setSelectedDeviceId}>
                        <Select.Trigger style={{ width: '100%' }} />
                        <Select.Content>
                          {devices.map((d) => (
                            <Select.Item key={d.device_id} value={d.device_id}>
                              {d.device_id} [{d.patient_name || 'No patient'}]
                            </Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    )}
                  </Box>

                  {/* Date and Time */}
                  <Flex gap="3">
                    <Box style={{ flex: 1 }}>
                      <Text size="2" weight="bold" mb="1" as="div">Date</Text>
                      <input
                        type="date"
                        value={bpDate}
                        onChange={(e) => setBpDate(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid var(--gray-7)',
                          fontSize: 14,
                          boxSizing: 'border-box',
                        }}
                      />
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Text size="2" weight="bold" mb="1" as="div">Time</Text>
                      <TimeInput12h
                        value={bpTime}
                        onChange={setBpTime}
                      />
                    </Box>
                  </Flex>

                  {/* BP values */}
                  <Flex gap="3">
                    <Box style={{ flex: 1 }}>
                      <Text size="2" weight="bold" mb="1" as="div">Systolic</Text>
                      <TextField.Root>
                        <TextField.Input
                          type="number"
                          value={String(systolic)}
                          onChange={(e) => setSystolic(Number(e.currentTarget.value))}
                          placeholder="120"
                        />
                      </TextField.Root>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Text size="2" weight="bold" mb="1" as="div">Diastolic</Text>
                      <TextField.Root>
                        <TextField.Input
                          type="number"
                          value={String(diastolic)}
                          onChange={(e) => setDiastolic(Number(e.currentTarget.value))}
                          placeholder="80"
                        />
                      </TextField.Root>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Text size="2" weight="bold" mb="1" as="div">Pulse</Text>
                      <TextField.Root>
                        <TextField.Input
                          type="number"
                          value={String(pulse)}
                          onChange={(e) => setPulse(Number(e.currentTarget.value))}
                          placeholder="72"
                        />
                      </TextField.Root>
                    </Box>
                  </Flex>

                  {/* Send button */}
                  <Button size="3" onClick={handleSimpleSend} disabled={simpleSending || !selectedDeviceId}>
                    <PaperPlaneIcon />
                    {simpleSending ? 'Sending...' : 'Send Blood Pressure'}
                  </Button>

                  {/* Response */}
                  {simpleResponse && (
                    <Box style={{
                      background: simpleResponse.success ? '#f0fdf4' : '#fef2f2',
                      border: `1px solid ${simpleResponse.success ? '#86efac' : '#fca5a5'}`,
                      borderRadius: 8,
                      padding: 16,
                    }}>
                      <Text size="2" weight="bold" style={{ color: simpleResponse.success ? '#16a34a' : '#dc2626' }}>
                        {simpleResponse.success ? 'Sent successfully' : 'Error'}
                      </Text>
                      <Text size="2" as="div" style={{ marginTop: 4 }}>
                        {simpleResponse.message}
                      </Text>
                    </Box>
                  )}
                </Flex>
              ) : (
                /* Advanced mode - existing functionality */
                <Flex direction="column" gap="3">
                  <Heading size="3">Send Test Webhook</Heading>

                  <Flex gap="3" align="center">
                    <Box style={{ width: 140 }}>
                      <Select.Root value={endpointType} onValueChange={(v) => handleEndpointChange(v as 'readings' | 'events')}>
                        <Select.Trigger />
                        <Select.Content>
                          <Select.Item value="readings">Readings</Select.Item>
                          <Select.Item value="events">Events</Select.Item>
                        </Select.Content>
                      </Select.Root>
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Select.Root value={selectedType} onValueChange={handleTypeChange}>
                        <Select.Trigger />
                        <Select.Content>
                          {typeOptions.map((t) => (
                            <Select.Item key={t} value={t}>{t}</Select.Item>
                          ))}
                        </Select.Content>
                      </Select.Root>
                    </Box>
                  </Flex>

                  <TextArea
                    value={payload}
                    onChange={(e) => setPayload(e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: '13px', minHeight: 300 }}
                  />

                  <Button onClick={handleAdvancedSend} disabled={sending}>
                    <PaperPlaneIcon />
                    {sending ? 'Sending...' : 'Send'}
                  </Button>

                  {lastResponse && (
                    <Box style={{ background: '#f8f9fa', borderRadius: 6, padding: 12 }}>
                      <Text size="1" style={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                        {lastResponse}
                      </Text>
                    </Box>
                  )}
                </Flex>
              )}
            </Flex>
          </Card>
        </Box>

        {/* Right: Status panel */}
        <Box style={{ flex: 1 }}>
          <Card>
            <Flex direction="column" gap="3">
              <Flex align="center" justify="between">
                <Heading size="3">Status</Heading>
                <Button variant="soft" size="1" onClick={fetchStatus} disabled={loadingStatus}>
                  <ReloadIcon />
                  Refresh
                </Button>
              </Flex>

              {status && (
                <>
                  <Box>
                    <Text size="2" weight="bold">Readings ({status.readings.total} total)</Text>
                    <Flex direction="column" gap="1" mt="2">
                      {status.readings.byType.length === 0 && (
                        <Text size="1" color="gray">No readings received yet</Text>
                      )}
                      {status.readings.byType.map((r) => (
                        <Flex key={r.reading_type} justify="between" align="center">
                          <Badge variant="soft">{r.reading_type}</Badge>
                          <Flex gap="2" align="center">
                            <Text size="1">{r.count} readings</Text>
                            <Text size="1" color="gray">
                              Last: {new Date(r.last_received).toLocaleString()}
                            </Text>
                          </Flex>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>

                  <Box>
                    <Text size="2" weight="bold">Events ({status.events.total} total)</Text>
                    <Flex direction="column" gap="1" mt="2">
                      {status.events.byType.length === 0 && (
                        <Text size="1" color="gray">No events received yet</Text>
                      )}
                      {status.events.byType.map((e) => (
                        <Flex key={e.event_type} justify="between" align="center">
                          <Badge variant="soft" color="orange">{e.event_type}</Badge>
                          <Flex gap="2" align="center">
                            <Text size="1">{e.count} events</Text>
                            <Text size="1" color="gray">
                              Last: {new Date(e.last_received).toLocaleString()}
                            </Text>
                          </Flex>
                        </Flex>
                      ))}
                    </Flex>
                  </Box>
                </>
              )}
            </Flex>
          </Card>
        </Box>
      </Flex>

      {/* PHA-1103: SmartMeter is a second RPM vendor writing into the same
          tables. Its readings need no separate viewer — they already show on the
          chart and in billing — but its delivery pipeline needs its own
          operational view, because every way it fails is silent. */}
      <Box mt="4">
        <SmartMeterPanel />
      </Box>
    </AdminLayout>
  );
};

export default AdminTellihealthPage;
